/**
 * Email notification preferences — purely client-side storage.
 *
 * IMPORTANT (per the project's CRITICAL RULE for the email-notifications
 * feature): we do NOT add database fields, modify schema, or change any
 * existing email/SMTP/Resend wiring. Preferences are stored in
 * localStorage, scoped per user id, so they persist across sessions on the
 * same device without touching the backend.
 *
 * Other modules can:
 *   - read prefs with `getEmailPrefs(userId)`
 *   - update with `setEmailPrefs(userId, partial)`
 *   - check a single channel quickly via `isChannelEnabled(userId, channel)`
 *   - subscribe to changes via `subscribeEmailPrefs(userId, cb)`
 *
 * The dispatcher in `emailNotifications.ts` consults these prefs before
 * ever attempting to invoke the existing send-* edge functions, so
 * disabling a channel here truly stops the email from being requested.
 */

export type EmailChannel =
  | 'important'        // Member joined via invite, profile claimed, etc.
  | 'weekly_digest'    // Weekly family update summary
  | 'birthdays'        // Birthday reminders
  | 'memory_activity'; // New photo/video/memory + comments/reactions

export interface EmailPreferences {
  important: boolean;
  weekly_digest: boolean;
  birthdays: boolean;
  memory_activity: boolean;
  /** Master switch — when false, ALL channels are silenced. */
  all_email_off: boolean;
  /** ISO timestamp of last update — used for UI feedback only. */
  updated_at: string | null;
}

export const DEFAULT_EMAIL_PREFS: EmailPreferences = {
  important: true,
  weekly_digest: true,
  birthdays: true,
  memory_activity: true,
  all_email_off: false,
  updated_at: null,
};

const KEY_PREFIX = 'kc.email.prefs.';
const ANON_KEY = 'kc.email.prefs.anon';

const storageKey = (userId: string | null | undefined) =>
  userId ? `${KEY_PREFIX}${userId}` : ANON_KEY;

const safeStorage = (): Storage | null => {
  try {
    if (typeof window === 'undefined') return null;
    return window.localStorage;
  } catch {
    return null;
  }
};

export const getEmailPrefs = (
  userId: string | null | undefined
): EmailPreferences => {
  const ls = safeStorage();
  if (!ls) return { ...DEFAULT_EMAIL_PREFS };
  try {
    const raw = ls.getItem(storageKey(userId));
    if (!raw) return { ...DEFAULT_EMAIL_PREFS };
    const parsed = JSON.parse(raw) as Partial<EmailPreferences>;
    return { ...DEFAULT_EMAIL_PREFS, ...parsed };
  } catch {
    return { ...DEFAULT_EMAIL_PREFS };
  }
};

export const setEmailPrefs = (
  userId: string | null | undefined,
  patch: Partial<EmailPreferences>
): EmailPreferences => {
  const current = getEmailPrefs(userId);
  const next: EmailPreferences = {
    ...current,
    ...patch,
    updated_at: new Date().toISOString(),
  };
  const ls = safeStorage();
  if (ls) {
    try {
      ls.setItem(storageKey(userId), JSON.stringify(next));
    } catch {
      /* quota exceeded or disabled — non-fatal */
    }
  }
  // Notify subscribers in this tab.
  notifyListeners(userId ?? null, next);
  return next;
};

export const resetEmailPrefs = (
  userId: string | null | undefined
): EmailPreferences => setEmailPrefs(userId, { ...DEFAULT_EMAIL_PREFS });

/**
 * Quick check used by the dispatcher. Always returns false if the master
 * "all email off" switch is on, regardless of the per-channel setting.
 */
export const isChannelEnabled = (
  userId: string | null | undefined,
  channel: EmailChannel
): boolean => {
  const prefs = getEmailPrefs(userId);
  if (prefs.all_email_off) return false;
  return Boolean(prefs[channel]);
};

// ---------- in-tab subscription pub/sub ----------

type Listener = (prefs: EmailPreferences) => void;
const listeners = new Map<string, Set<Listener>>();

const notifyListeners = (userId: string | null, prefs: EmailPreferences) => {
  const key = userId ?? '__anon__';
  const set = listeners.get(key);
  if (!set) return;
  set.forEach((cb) => {
    try {
      cb(prefs);
    } catch {
      /* swallow listener errors */
    }
  });
};

export const subscribeEmailPrefs = (
  userId: string | null | undefined,
  cb: Listener
): (() => void) => {
  const key = userId ?? '__anon__';
  let set = listeners.get(key);
  if (!set) {
    set = new Set();
    listeners.set(key, set);
  }
  set.add(cb);
  return () => {
    set?.delete(cb);
  };
};
